import { useCallback, useEffect, useState } from "react";
import TabComponent from "./sections/TabComponent";
import { useNavigate } from "react-router-dom";
import Layout from "components/layout/Layout";
import { useIntl } from "react-intl";
import history from "history/browser";
import OrderThumbnail from "components/reservation/OrderThumbnail";
import { appendMyOrders } from "app/myOrdersSlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import useIntersect from "hooks/useIntersect";
import ScrollToTopButton from "components/ScrollToTopButton";
import UserService from "services/userService";
import OrderSkeleton from "components/skeleton/OrderSkeleton";
import { getContextualUrl } from "utils/navigation";

const OrderHistoryPage = () => {
  const dispatch = useAppDispatch();
  const { orders, filterSettings, last, number } = useAppSelector(
    (state) => state.myOrders
  );

  // TODO: 여행 상품 서비스 종료 후, 한번에 두 유형 모두 조회할 수 있게 변경
  let filteredOrders = orders;

  const intl = useIntl();
  const [isFetching, setIsFetching] = useState(false);
  const [isScrollToTopButtonShow, setIsScrollToTopButtonShow] = useState(false);
  const navigate = useNavigate();

  const infiniteScrollingTarget = useIntersect(async (entry, observer) => {
    observer.unobserve(entry.target);
    if (!last && !isFetching) {
      fetchOrdersInfo();
    }
  });

  const scrollToTopButtonTarget = useIntersect(
    () => setIsScrollToTopButtonShow(false),
    () => setIsScrollToTopButtonShow(true)
  );

  const fetchOrdersInfo = useCallback(async () => {
    setIsFetching(true);

    try {
      const response = await UserService.getMyOrders(
        filterSettings?.fromDate,
        filterSettings?.toDate,
        number + 1,
        filterSettings?.status
      );

      dispatch(appendMyOrders(response));
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsFetching(false);
    }
  }, [
    dispatch,
    filterSettings?.fromDate,
    filterSettings?.status,
    filterSettings?.toDate,
    number,
  ]);

  // const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  // const handleFetchMoreOrders = useCallback(() => {
  //   if (!timeoutId.current) {
  //     timeoutId.current = setTimeout(() => {
  //       timeoutId.current = null;
  //       const { scrollTop, offsetHeight } = document.documentElement;
  //       if (
  //         !last &&
  //         window.visualViewport &&
  //         window.visualViewport.height + scrollTop >= offsetHeight * 0.9
  //       ) {
  //         setIsFetching(true);
  //         fetchOrdersInfo();
  //         return;
  //       }
  //       if (last) {
  //         setIsFetching(false);
  //       }
  //     }, 1000);
  //   }
  // }, [fetchOrdersInfo, last]);

  const moveToMyPage = useCallback(() => {
    navigate(getContextualUrl("/user-settings"), { replace: true });
  }, [navigate]);

  useEffect(() => {
    // First order history load
    if (number === -1) {
      fetchOrdersInfo();
    }

    const historyEvent = history.listen(({ action }) => {
      if (action === "POP") {
        moveToMyPage();
      }
    });
    return () => {
      historyEvent();
    };
  }, [dispatch, fetchOrdersInfo, moveToMyPage, number]);

  return (
    <Layout
      text={intl.formatMessage({ id: "orders.title" })}
      onBack={moveToMyPage}
    >
      <TabComponent ref={scrollToTopButtonTarget} />
      <section className="mb-2 space-y-2">
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order, index) => {
            return <OrderThumbnail key={index} order={order} />;
          })
        ) : number === -1 ? (
          <OrderSkeleton number={10} />
        ) : (
          <div className="m-auto flex min-h-[50vh] items-center justify-center text-center">
            <p className="text-2xl font-bold">
              {intl.formatMessage({ id: "orders.empty" })}
            </p>
          </div>
        )}
      </section>
      {isFetching && orders.length > 0 && <OrderSkeleton number={10} />}
      {isScrollToTopButtonShow && <ScrollToTopButton />}
      <div ref={infiniteScrollingTarget}></div>
    </Layout>
  );
};

export default OrderHistoryPage;
